/**
 * WebSocket Service
 *
 * Manages WebSocket connections for real-time data updates
 * Handles connection management, message routing, and error handling
 */

import type { RealtimeData, RealtimeMetrics } from '@/types/analytics';

// ============================================================================
// Types
// ============================================================================

export interface WebSocketMessage {
  type: string;
  payload: any;
  timestamp: string;
  id?: string;
}

export interface WebSocketConfig {
  url?: string;
  protocols?: string | string[];
  maxRetries?: number;
  retryDelay?: number;
  heartbeatInterval?: number;
  enableHeartbeat?: boolean;
}

export type WebSocketStatus =
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'error'
  | 'reconnecting';

export type MessageHandler = (message: WebSocketMessage) => void;
export type StatusHandler = (status: WebSocketStatus) => void;
export type ErrorHandler = (error: Event | Error) => void;

// ============================================================================
// WebSocket Service Class
// ============================================================================

class WebSocketService {
  private ws: WebSocket | null = null;
  private status: WebSocketStatus = 'disconnected';
  private config: Required<WebSocketConfig>;
  private retryCount = 0;
  private heartbeatTimer: number | null = null;
  private reconnectTimer: number | null = null;

  // Event handlers
  private messageHandlers = new Map<string, MessageHandler[]>();
  private statusHandlers: StatusHandler[] = [];
  private errorHandlers: ErrorHandler[] = [];

  // Default configuration
  private defaultConfig: Required<WebSocketConfig> = {
    url: this.getWebSocketUrl(),
    protocols: [],
    maxRetries: 5,
    retryDelay: 3000,
    heartbeatInterval: 30000,
    enableHeartbeat: true,
  };

  constructor(config: WebSocketConfig = {}) {
    this.config = { ...this.defaultConfig, ...config };
  }

  // ============================================================================
  // Public Methods
  // ============================================================================

  /**
   * Connect to WebSocket server
   */
  connect(): void {
    if (this.ws && this.status === 'connected') {
      return;
    }

    this.setStatus('connecting');

    try {
      this.ws = new WebSocket(this.config.url, this.config.protocols);
      this.setupEventListeners();
    } catch (error) {
      this.handleError(error as Error);
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.clearTimers();
    this.retryCount = 0;

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }

    this.setStatus('disconnected');
  }

  /**
   * Send message to WebSocket server
   */
  send(message: Omit<WebSocketMessage, 'timestamp' | 'id'>): boolean {
    if (!this.ws || this.status !== 'connected') {
      return false;
    }

    const fullMessage: WebSocketMessage = {
      ...message,
      timestamp: new Date().toISOString(),
      id: this.generateId(),
    };

    try {
      this.ws.send(JSON.stringify(fullMessage));
      return true;
    } catch (error) {
      this.handleError(error as Error);
      return false;
    }
  }

  /**
   * Subscribe to messages of a specific type
   */
  on(messageType: string, handler: MessageHandler): () => void {
    if (!this.messageHandlers.has(messageType)) {
      this.messageHandlers.set(messageType, []);
    }

    const handlers = this.messageHandlers.get(messageType)!;
    handlers.push(handler);

    // Return unsubscribe function
    return () => {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to status changes
   */
  onStatusChange(handler: StatusHandler): () => void {
    this.statusHandlers.push(handler);

    return () => {
      const index = this.statusHandlers.indexOf(handler);
      if (index > -1) {
        this.statusHandlers.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to errors
   */
  onError(handler: ErrorHandler): () => void {
    this.errorHandlers.push(handler);

    return () => {
      const index = this.errorHandlers.indexOf(handler);
      if (index > -1) {
        this.errorHandlers.splice(index, 1);
      }
    };
  }

  /**
   * Get current connection status
   */
  getStatus(): WebSocketStatus {
    return this.status;
  }

  /**
   * Get connection info
   */
  getConnectionInfo() {
    return {
      status: this.status,
      url: this.config.url,
      retryCount: this.retryCount,
      maxRetries: this.config.maxRetries,
      readyState: this.ws?.readyState,
    };
  }

  // ============================================================================
  // Private Methods
  // ============================================================================

  private getWebSocketUrl(): string {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;

    // In production, this would be your actual WebSocket endpoint
    // For development, we'll use a mock URL
    if (import.meta.env.DEV) {
      return `${protocol}//${host}/ws/analytics`;
    }

    return `${protocol}//api.veggieworld.africa/ws/analytics`;
  }

  private setupEventListeners(): void {
    if (!this.ws) return;

    this.ws.onopen = this.handleOpen.bind(this);
    this.ws.onmessage = this.handleMessage.bind(this);
    this.ws.onclose = this.handleClose.bind(this);
    this.ws.onerror = this.handleError.bind(this);
  }

  private handleOpen(): void {
    // info: WebSocket connected
    this.setStatus('connected');
    this.retryCount = 0;

    if (this.config.enableHeartbeat) {
      this.startHeartbeat();
    }

    // Send authentication if needed
    this.send({
      type: 'auth',
      payload: {
        token: localStorage.getItem('token'), // Or get from your auth context
      },
    });
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const message: WebSocketMessage = JSON.parse(event.data);

      // Handle system messages
      if (message.type === 'pong') {
        return; // Heartbeat response
      }

      // Route message to handlers
      const handlers = this.messageHandlers.get(message.type) || [];
      handlers.forEach(handler => {
        try {
          handler(message);
        } catch (error) {
          // warn: error in message handler (suppressed to avoid noisy logs)
        }
      });
    } catch (error) {
      // warn: error parsing WebSocket message (handled downstream)
      this.handleError(error as Error);
    }
  }

  private handleClose(event: CloseEvent): void {
    // info: WebSocket disconnected: code/reason available in event
    this.clearTimers();

    if (event.code !== 1000 && this.retryCount < this.config.maxRetries) {
      this.setStatus('reconnecting');
      this.scheduleReconnect();
    } else {
      this.setStatus('disconnected');
    }
  }

  private handleError(error: Event | Error): void {
    // warn: WebSocket error (status set to error, handlers notified)
    this.setStatus('error');

    this.errorHandlers.forEach(handler => {
      try {
        handler(error);
      } catch (handlerError) {
        // warn: error in error handler (suppressed)
      }
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;

    const delay = this.config.retryDelay * Math.pow(2, this.retryCount);

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.retryCount++;
      // info: reconnecting... attempt count updated
      this.connect();
    }, delay);
  }

  private startHeartbeat(): void {
    if (this.heartbeatTimer) return;

    this.heartbeatTimer = window.setInterval(() => {
      if (this.status === 'connected') {
        this.send({
          type: 'ping',
          payload: {},
        });
      }
    }, this.config.heartbeatInterval);
  }

  private clearTimers(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private setStatus(status: WebSocketStatus): void {
    if (this.status !== status) {
      this.status = status;
      this.statusHandlers.forEach(handler => {
        try {
          handler(status);
        } catch (error) {
          // warn: error in status handler (suppressed)
        }
      });
    }
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

// ============================================================================
// Specialized Analytics WebSocket Service
// ============================================================================

export class AnalyticsWebSocketService extends WebSocketService {
  constructor() {
    super({
      url: import.meta.env.DEV
        ? 'ws://localhost:3001/ws/analytics'
        : 'wss://api.veggieworld.africa/ws/analytics',
      heartbeatInterval: 30000,
      maxRetries: 3,
      retryDelay: 2000,
    });
  }

  /**
   * Subscribe to real-time analytics data
   */
  subscribeToAnalytics(handler: (data: RealtimeData) => void): () => void {
    return this.on('analytics_data', message => {
      handler(message.payload as RealtimeData);
    });
  }

  /**
   * Subscribe to real-time metrics
   */
  subscribeToMetrics(handler: (metrics: RealtimeMetrics) => void): () => void {
    return this.on('analytics_metrics', message => {
      handler(message.payload as RealtimeMetrics);
    });
  }

  /**
   * Subscribe to system alerts
   */
  subscribeToAlerts(handler: (alert: any) => void): () => void {
    return this.on('system_alert', message => {
      handler(message.payload);
    });
  }

  /**
   * Request specific data updates
   */
  requestUpdate(dataType: string, filters?: any): boolean {
    return this.send({
      type: 'request_update',
      payload: {
        dataType,
        filters,
      },
    });
  }

  /**
   * Start real-time monitoring for specific metrics
   */
  startMonitoring(metrics: string[]): boolean {
    return this.send({
      type: 'start_monitoring',
      payload: {
        metrics,
      },
    });
  }

  /**
   * Stop real-time monitoring
   */
  stopMonitoring(): boolean {
    return this.send({
      type: 'stop_monitoring',
      payload: {},
    });
  }
}

// ============================================================================
// Export singleton instance
// ============================================================================

export const analyticsWebSocket = new AnalyticsWebSocketService();
export default WebSocketService;
